/**
 * Master-family tags (Equipment / Pipeline / Structural masters) are drawn - 3D icons and 360
 * hotspots - from per-family catalogs. On load the API fills each catalog only with masters that
 * are harvested here or positioned somewhere (MasterTagService.GetMasterTagsQueryable). A master
 * that was neither is absent, and Set Position only PATCHED catalog entries
 * (APPLY_AUTHORITATIVE_TAG_REFRESH patches, never inserts). So a master placed from a 360 scan
 * stayed undrawn - in every other scan too - until the facility was reloaded; zoom-to only showed
 * it because it draws its own highlight marker (AVA-624).
 *
 * This decides whether a refreshed master must be ADDED to its catalog, and with which fields.
 */

export const MASTER_CATALOG_BUCKETS = Object.freeze({
  Equipment: 'allExistingAutomaticDbids',
  Pipeline: 'allExistingPipelineDbIds',
  Structural: 'allExistingStructuralDbIds',
});

// The fields the catalog rows carry (TagDbIdInfo on the API, ForgeTagShortInfo here).
const CATALOG_FIELDS = Object.freeze([
  'id',
  'name',
  'thumbnailUrl',
  'point',
  'positionImage360Id',
  'positionedFacilityIds',
  'children',
  'spatialTargets',
  'condition',
]);

const normalizedId = (value) => String(value).trim().toLowerCase();

const hasFinitePoint = (point) => !!point
  && [point.x, point.y, point.z].every(Number.isFinite);

function tagType(tag) {
  return typeof tag?.getType === 'function' ? tag.getType() : tag?.type;
}

/**
 * @param {Object} refreshedTag - The authoritative tag returned after a position write
 * @param {Object} catalogs - Object holding the catalog arrays (the TAGS module state)
 * @returns {{bucket: string, type: string, fields: Object}|null} What to add, or null when the
 *   tag is not a placed master or its catalog already has it (that row is patched elsewhere).
 */
export function missingMasterCatalogEntry(refreshedTag, catalogs) {
  if (refreshedTag?.isMaster !== true || refreshedTag.id == null) return null;
  const type = tagType(refreshedTag);
  const bucket = MASTER_CATALOG_BUCKETS[type];
  if (!bucket || !hasFinitePoint(refreshedTag.point)) return null;
  const catalog = catalogs?.[bucket];
  if (!Array.isArray(catalog)) return null;
  const id = normalizedId(refreshedTag.id);
  if (catalog.some((row) => row?.id != null && normalizedId(row.id) === id)) return null;

  const fields = {};
  CATALOG_FIELDS.forEach((key) => {
    const value = refreshedTag[key];
    if (value !== undefined) fields[key] = Array.isArray(value) ? [...value] : value;
  });
  return { bucket, type, fields };
}
